// qs-store-panels.ts
import type {
  PanelConfig,
  PanelPosition,
  PanelState,
  PanelType,
  VerticalPanelConfig
} from '@/types/qs-type-panels'
import { useStorage } from '@vueuse/core'
import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

export const usePanelStore = defineStore('panels', () => {
  const panels = useStorage<Record<string, PanelState>>('vectorsketch-panels', {})
  const zIndexCounter = useStorage('vectorsketch-panels-z-index', 1000)
  const panelDimensionsCache = ref<Record<string, { width: number; height: number }>>({})

  const panelConfigs: Record<PanelType, PanelConfig> = {
    'brush-size': {
      id: 'brush-size',
      title: 'Size',
      type: 'vertical',
      width: 'auto',
      defaultPosition: { x: 0, y: 0 }
    } as VerticalPanelConfig,
    colors: {
      id: 'colors',
      title: 'Colors',
      type: 'vertical',
      width: 'auto',
      defaultPosition: { x: 0, y: 0 }
    } as VerticalPanelConfig,
    file: {
      id: 'file',
      title: 'File',
      type: 'vertical',
      width: 'auto',
      defaultPosition: { x: 0, y: 0 }
    } as VerticalPanelConfig,
    'brush-type': {
      id: 'brush-type',
      title: 'Type',
      type: 'vertical',
      width: 'auto',
      defaultPosition: { x: 0, y: 0 }
    } as VerticalPanelConfig,
    layers: {
      id: 'layers',
      title: 'Layers',
      type: 'vertical',
      width: 'auto',
      height: 'auto',
      defaultPosition: { x: 0, y: 0 }
    } as VerticalPanelConfig,
    navigation: {
      id: 'navigation',
      title: 'Navigation',
      type: 'vertical',
      width: 'auto',
      defaultPosition: { x: 0, y: 0 }
    } as VerticalPanelConfig,
    mode: {
      id: 'mode',
      title: 'Mode',
      type: 'vertical',
      width: 'auto',
      defaultPosition: { x: 0, y: 0 }
    } as VerticalPanelConfig,
    performance: {
      id: 'performance',
      title: 'Performance',
      type: 'vertical',
      width: 'auto',
      height: 'auto',
      defaultPosition: { x: 0, y: 0 }
    } as VerticalPanelConfig,
    text: {
      id: 'text',
      title: 'Text',
      type: 'vertical',
      width: 'auto',
      height: 'auto',
      defaultPosition: { x: 0, y: 0 }
    } as VerticalPanelConfig,
    smooth: {
      id: 'smooth',
      title: 'Smooth',
      type: 'vertical',
      width: 'auto',
      defaultPosition: { x: 0, y: 0 }
    } as VerticalPanelConfig
  }

  const visiblePanels = computed(() =>
    Object.values(panels.value).filter(panel => panel.visible)
  )

  const panelOrder = computed(() =>
    visiblePanels.value.sort((a, b) => b.zIndex - a.zIndex)
  )

  const initializePanels = () => {
    if (Object.keys(panels.value).length > 0) {
      const isValid = Object.keys(panelConfigs).every(id =>
        panels.value[id] &&
        panels.value[id]?.position &&
        typeof panels.value[id]?.position.x === 'number' &&
        typeof panels.value[id]?.position.y === 'number'
      )

      if (isValid) {
        Object.keys(panels.value).forEach(panelId => {
          const panel = panels.value[panelId]
          if (panel?.position) {
            panel.position = constrainToViewport(panelId, panel.position)
          }
        })
        return
      }
    }

    createDefaultPanels()
  }

  const calculateDefaultPosition = (panelId: string, dimensions: { width: number; height: number }): PanelPosition => {
    const minDistance = 10
    const viewportWidth = Math.max(400, window.innerWidth)
    const viewportHeight = Math.max(300, window.innerHeight)

    switch (panelId) {
      case 'file':
        return {
          x: minDistance,
          y: minDistance
        }
      case 'colors':
        return {
          x: Math.max(minDistance, (viewportWidth - dimensions.width) / 2),
          y: minDistance
        }
      case 'layers':
        return {
          x: minDistance,
          y: Math.max(minDistance, (viewportHeight - dimensions.height) / 2)
        }
      case 'brush-type':
        return {
          x: Math.max(minDistance, viewportWidth - dimensions.width - minDistance),
          y: Math.max(minDistance, (viewportHeight - dimensions.height) / 2)
        }
      case 'brush-size':
        return {
          x: Math.max(minDistance, viewportWidth - dimensions.width - minDistance),
          y: minDistance
        }
      case 'navigation':
        return {
          x: minDistance,
          y: Math.max(minDistance, viewportHeight - dimensions.height - minDistance)
        }
      case 'mode':
        return {
          x: Math.max(minDistance, (viewportWidth - dimensions.width) / 2),
          y: Math.max(minDistance, viewportHeight - dimensions.height - minDistance)
        }
      case 'performance':
        return {
          x: Math.max(minDistance, viewportWidth - dimensions.width - minDistance),
          y: Math.max(minDistance, viewportHeight - dimensions.height - minDistance)
        }
      case 'text': {
        const yCenter = Math.max(minDistance, (viewportHeight - dimensions.height) / 2)
        const yBottom = Math.max(minDistance, viewportHeight - dimensions.height - minDistance)
        return {
          x: minDistance,
          y: Math.max(minDistance, (yCenter + yBottom) / 2)
        }
      }
      case 'smooth': {
        const yTop = minDistance
        const yCenter = Math.max(minDistance, (viewportHeight - dimensions.height) / 2)
        return {
          x: Math.max(minDistance, viewportWidth - dimensions.width - minDistance),
          y: Math.max(minDistance, (yTop + yCenter) / 2)
        }
      }
      default:
        return {
          x: Math.max(minDistance, (viewportWidth - dimensions.width) / 2),
          y: Math.max(minDistance, (viewportHeight - dimensions.height) / 2)
        }
    }
  }

  const createDefaultPanels = () => {
    Object.entries(panelConfigs).forEach(([type, config]) => {
      const safePosition = { x: 100, y: 100 }
      const isVisible = type !== 'performance'

      panels.value[type] = {
        id: config.id,
        title: config.title,
        visible: isVisible,
        position: safePosition,
        zIndex: zIndexCounter.value++
      }
    })

    setTimeout(() => {
      correctPanelPositionsAfterRender()
    }, 100)
  }

  const correctPanelPositionsAfterRender = () => {
    Object.keys(panelConfigs).forEach(panelId => {
      const panel = panels.value[panelId]
      if (panel) {
        const realDimensions = getRealPanelDimensions(panelId)
        if (realDimensions) {
          const optimalPosition = calculateDefaultPosition(panelId, realDimensions)
          const constrainedPosition = constrainToViewport(panelId, optimalPosition)
          panel.position = constrainedPosition
        }
      }
    })
  }

  const showPanel = (panelId: string) => {
    const panel = panels.value[panelId]
    if (panel) {
      panel.visible = true
      bringToFront(panelId)

      if (panelId === 'performance') {
        setTimeout(() => {
          const realDimensions = getRealPanelDimensions(panelId)
          if (realDimensions) {
            const optimalPosition = calculateDefaultPosition(panelId, realDimensions)
            const constrainedPosition = constrainToViewport(panelId, optimalPosition)
            panel.position = constrainedPosition

          }
        }, 50) // Short delay to ensure DOM is updated
      }
    }
  }

  const hidePanel = (panelId: string) => {
    const panel = panels.value[panelId]
    if (panel) {
      panel.visible = false
    }
  }

  const togglePanel = (panelId: string) => {
    const panel = panels.value[panelId]
    if (panel) {
      if (panel.visible) {
        hidePanel(panelId)
      } else {
        showPanel(panelId)
      }
    }
  }

  const bringToFront = (panelId: string) => {
    const panel = panels.value[panelId]
    if (panel) {
      panel.zIndex = zIndexCounter.value++
    }
  }

  const updatePanelPosition = (panelId: string, position: PanelPosition) => {
    const panel = panels.value[panelId]
    if (panel) {
      const constrainedPosition = constrainToViewport(panelId, position)
      panel.position = constrainedPosition
    }
  }

  const resetPanelPositions = () => {
    Object.entries(panelConfigs).forEach(([type]) => {
      const panel = panels.value[type]
      if (panel) {
        const dimensions = getPanelDimensions(type)
        if (!dimensions) return

        const calculatedPosition = calculateDefaultPosition(type, dimensions)
        const constrainedPos = constrainToViewport(type, calculatedPosition)
        panel.position = constrainedPos
      }
    })
  }

  const recalculatePerformancePanelPosition = () => {
    const panelId = 'performance'
    const panel = panels.value[panelId]
    if (panel) {
      delete panelDimensionsCache.value[panelId]

      const dimensions = getPanelDimensions(panelId)
      if (dimensions) {
        const newPosition = calculateDefaultPosition(panelId, dimensions)
        const constrainedPosition = constrainToViewport(panelId, newPosition)
        panel.position = constrainedPosition
      }
    }
  }

  const getPanelConfig = (panelId: string): PanelConfig | null => {
    return panelConfigs[panelId as PanelType] || null
  }

  const getPanelDimensions = (panelId: string) => {
    const realDimensions = getRealPanelDimensions(panelId)
    if (realDimensions) {
      return realDimensions
    }

    if (panelDimensionsCache.value[panelId]) {
      return panelDimensionsCache.value[panelId]
    }

    const config = getPanelConfig(panelId)
    if (!config) return { width: 0, height: 0 }

    const dimensions = getFallbackDimensions(panelId)
    panelDimensionsCache.value[panelId] = dimensions
    return dimensions
  }

  const getRealPanelDimensions = (panelId: string): { width: number; height: number } | null => {
    try {
      const panelElement = document.querySelector(`[data-panel-id="${panelId}"]`) as HTMLElement
      if (!panelElement) return null

      const rect = panelElement.getBoundingClientRect()
      return {
        width: rect.width,
        height: rect.height
      }
    } catch {
      return null
    }
  }

  const getFallbackDimensions = (panelId: string): { width: number; height: number } => {
    switch (panelId) {
      case 'performance':
        return { width: 300, height: 200 }
      case 'layers':
        return { width: 200, height: 200 }
      case 'mode':
        return { width: 80, height: 20 }
      case 'file':
        return { width: 60, height: 160 }
      case 'colors':
        return { width: 264, height: 44 }
      case 'brushSize':
        return { width: 132, height: 60 }
      case 'brushType':
        return { width: 44, height: 60 }
      case 'navigation':
        return { width: 200, height: 60 }
      case 'text':
        return { width: 200, height: 60 }
      case 'smooth':
        return { width: 44, height: 132 }
      default:
        return { width: 150, height: 100 }
    }
  }

  const updateLayerCount = () => {
    invalidatePanelDimensions('layers')
  }

  const updatePanelDimensionsCache = (panelId: string, dimensions: { width: number; height: number }) => {
    panelDimensionsCache.value[panelId] = dimensions
  }

  const invalidatePanelDimensions = (panelId: string) => {
    delete panelDimensionsCache.value[panelId]

    const panel = panels.value[panelId]
    if (panel) {
      const currentPosition = panel.position
      const constrainedPosition = constrainToViewport(panelId, currentPosition)
      panel.position = constrainedPosition
    }
  }

  const refreshPanelDimensions = (panelId: string) => {
    delete panelDimensionsCache.value[panelId]

    const newDimensions = getRealPanelDimensions(panelId)
    if (newDimensions) {
      panelDimensionsCache.value[panelId] = newDimensions

      const panel = panels.value[panelId]
      if (panel) {
        const currentPosition = panel.position
        const constrainedPosition = constrainToViewport(panelId, currentPosition)
        panel.position = constrainedPosition
      }
    }
  }

  const constrainToViewport = (panelId: string, position: PanelPosition): PanelPosition => {
    const dimensions = getPanelDimensions(panelId)
    const minDistance = 10
    const viewportWidth = Math.max(400, window.innerWidth)
    const viewportHeight = Math.max(300, window.innerHeight)
    const validWidth = Math.max(50, dimensions.width)
    const validHeight = Math.max(50, dimensions.height)

    return {
      x: Math.max(minDistance, Math.min(position.x, viewportWidth - validWidth - minDistance)),
      y: Math.max(minDistance, Math.min(position.y, viewportHeight - validHeight - minDistance))
    }
  }

  return {
    panels,
    visiblePanels,
    panelOrder,
    initializePanels,
    showPanel,
    hidePanel,
    togglePanel,
    bringToFront,
    updatePanelPosition,
    resetPanelPositions,
    correctPanelPositionsAfterRender,
    recalculatePerformancePanelPosition,
    getPanelConfig,
    getPanelDimensions,
    updatePanelDimensionsCache,
    invalidatePanelDimensions,
    refreshPanelDimensions,
    constrainToViewport,
    updateLayerCount
  }
})